<template>
    <div
        ref="containerRef"
        :class="$style.container"
    >
        <span
            ref="spanRef"
            :class="$style.text"
        />
    </div>
</template>
<script lang="ts" setup>
import { TransitionPresets, useDebounceFn, useResizeObserver, useTransition } from '@vueuse/core'
import { computed, onMounted, ref, useCssModule, watch } from 'vue'

import { formatValue } from '@/utils/numbers'

const props = defineProps<{
    balance: number
    isAnimated?: boolean
    isColorBalance?: boolean
}>()

const styles = useCssModule()

function getValues({ maxChars, value }: { maxChars: number; value: number }): {
    display: string
    tooltip: string | undefined
} {
    const displayFullValue = formatValue(value, { decimals: 2, minDecimals: 2 })
    const displayFullValueChars = displayFullValue.length
    if (maxChars < displayFullValueChars) {
        return {
            display: formatValue(value, { shorten: true, decimals: 2, minDecimals: 2 }),
            tooltip: displayFullValue,
        }
    }
    return { display: displayFullValue, tooltip: undefined }
}

const tooltipContent = ref<string | undefined>(undefined)
const containerRef = ref<HTMLElement | null>(null)
const spanRef = ref<HTMLElement | null>(null)
const maxChars = ref<number | undefined>(undefined)
const computedBalance = computed(() => props.balance) // to enable reactivity on useTransition
const animatedBalance = useTransition(computedBalance, {
    duration: 1000,
    disabled: !props.isAnimated,
    transition: TransitionPresets.linear,
    onStarted: () => {
        spanRef.value?.classList.add(styles.bouncing)
    },
    onFinished: () => {
        spanRef.value?.classList.remove(styles.bouncing)
    },
})

function renderValues() {
    if (maxChars.value === undefined || !spanRef.value) {
        return
    }

    const { display, tooltip } = getValues({
        maxChars: maxChars.value,
        value: Math.round(animatedBalance.value * 100) / 100,
    })
    tooltipContent.value = tooltip

    if (props.isColorBalance) {
        const dotIndex = display.indexOf('.')
        if (dotIndex !== -1) {
            const intPart = display.slice(0, dotIndex)
            const decPart = display.slice(dotIndex)
            spanRef.value.innerHTML = `<span class="${styles.balanceInt}">${intPart}</span><span class="${styles.balanceDec}">${decPart}</span>`
        } else {
            spanRef.value.innerHTML = `<span class="${styles.balanceInt}">${display}</span>`
        }
    } else {
        spanRef.value.textContent = display
    }
}

watch(animatedBalance, renderValues)

function onResize() {
    const container = containerRef.value
    const span = spanRef.value
    if (!container || !span) {
        return
    }

    span.textContent = ''
    const availableWidth = container.clientWidth
    span.textContent = 'X' // 1 wide character to measure
    const charWidth = span.clientWidth

    maxChars.value = Math.floor(availableWidth / charWidth)

    renderValues()
}

const debouncedOnResize = useDebounceFn(onResize, 100)
useResizeObserver(containerRef, debouncedOnResize)

onMounted(() => {
    onResize()
})
</script>
<style module>
.container {
}

.text {
    display: inline-block;
}

.text {
    /* turn font into monospace for less shaking animation */
    font-variant-numeric: tabular-nums;
}

.bouncing {
    animation-name: bouncing;
    animation-delay: 0.1s;
    animation-duration: 0.2s;
    animation-iteration-count: infinite;
}
@keyframes bouncing {
    from {
        transform: scale(1);
    }
    to {
        transform: scale(1.1, 1.2);
    }
}

.balanceInt {
    color: #ffdca1;
}

.balanceDec {
    color: #6e6e6f;
}
</style>
